import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PushNotifications } from '@capacitor/push-notifications';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getNotificationNavigationUrl } from '@/utils/notificationNavigation';
import { decrementAppBadge, syncAppBadgeToUnread } from '@/utils/appBadge';
import { logger } from '@/utils/logger';

export function usePushNotifications(userId: string | undefined) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // "userId:token" of the last successful save — the native side re-injects the
  // token on every foreground (by design, for delivery reliability), so writes
  // must be deduped here.
  const lastSavedFcmKeyRef = useRef<string | null>(null);

  useEffect(() => {
    console.log('[PushNotif] hook fired — userId:', userId, 'isNative:', Capacitor.isNativePlatform());
    if (!userId || !Capacitor.isNativePlatform()) return;

    async function register() {
      try {
        console.log('[PushNotif] requesting permissions...');
        const { receive } = await PushNotifications.requestPermissions();
        console.log('[PushNotif] permission result:', receive);
        if (receive !== 'granted') {
          logger.warn('usePushNotifications', 'Push notification permission denied');
          return;
        }
        await PushNotifications.register();
      } catch (err) {
        logger.error('usePushNotifications register', err);
        console.error('[PushNotif] register error:', err);
      }
    }

    const tokenListenerPromise = PushNotifications.addListener('registration', async ({ value: token }) => {
      console.log('[PushNotif] token received:', token);
      // On iOS this event carries the RAW APNs device token (hex) — saving it
      // would clobber the FCM token and break sends (the server pushes via FCM).
      // The correct iOS token arrives via the AppDelegate 'capacitor:fcmToken'
      // bridge instead. Android's registration event is already the FCM token.
      if (Capacitor.getPlatform() === 'ios') {
        console.log('[PushNotif] iOS raw APNs token ignored; waiting for FCM bridge token');
        return;
      }
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ push_token: token })
          .eq('id', userId);
        if (error) {
          console.error('[PushNotif] token save error:', error);
          logger.error('usePushNotifications token save', error);
        } else {
          console.log('[PushNotif] token saved to DB successfully');
        }
      } catch (err) {
        console.error('[PushNotif] token save exception:', err);
        logger.error('usePushNotifications token save', err);
      }
    });

    const errorListenerPromise = PushNotifications.addListener('registrationError', (err) => {
      console.error('[PushNotif] registrationError:', JSON.stringify(err));
      logger.error('usePushNotifications registrationError', err);
    });

    const tapListenerPromise = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      async ({ notification }) => {
        const data = notification.data as Record<string, unknown>;
        const type = typeof data['type'] === 'string' ? data['type'] : '';
        const url = getNotificationNavigationUrl({ type, data });
        if (url) navigateRef.current(url);

        // Tapping a push means the user has read that one notification.
        // Instant cosmetic decrement first, then mark the row read and let the
        // authoritative unread-count sync settle the true number.
        void decrementAppBadge();
        try {
          const notificationId = data['notification_id'];
          if (typeof notificationId === 'string' && notificationId) {
            await supabase
              .from('notifications')
              .update({ read: true })
              .eq('id', notificationId)
              .eq('user_id', userId);
          }
        } catch (err) {
          logger.error('usePushNotifications mark tapped notification read', err);
        }
        void syncAppBadgeToUnread(userId);
      },
    );

    // iOS with Firebase: Firebase intercepts the APNs token and emits an FCM token
    // via MessagingDelegate in AppDelegate, which dispatches this custom DOM event.
    async function handleFCMToken(e: Event) {
      const token = (e as CustomEvent<string>).detail;
      if (!token) return;
      // Keyed by user too: the same device token must be re-saved if a
      // different account logs in on this device.
      const saveKey = `${userId}:${token}`;
      if (lastSavedFcmKeyRef.current === saveKey) return;
      console.log('[PushNotif] FCM token from iOS bridge:', token.substring(0, 20) + '...');
      try {
        const { error } = await supabase.from('profiles').update({ push_token: token }).eq('id', userId);
        if (error) console.error('[PushNotif] FCM token save error:', error);
        else {
          lastSavedFcmKeyRef.current = saveKey;
          console.log('[PushNotif] FCM token saved to DB');
        }
      } catch (err) {
        console.error('[PushNotif] FCM token save exception:', err);
      }
    }

    window.addEventListener('capacitor:fcmToken', handleFCMToken);

    // On iOS the native FCM-token event usually fires before login completes and
    // this listener exists — AppDelegate stashes the token on window so late
    // subscribers (us) can pick it up and save it for the now-known user.
    const stashedToken = (window as { __volyxFcmToken?: string }).__volyxFcmToken;
    if (stashedToken) {
      void handleFCMToken(new CustomEvent('capacitor:fcmToken', { detail: stashedToken }));
    }

    void register();

    // Keep the icon badge converged with the true unread count: once on app
    // start (covers cold-start-by-tap, where pushNotificationActionPerformed
    // can fire before our listener attaches) and on every return to foreground
    // (covers notifications read on another device/web while this app was
    // backgrounded).
    void syncAppBadgeToUnread(userId);
    const appStateListenerPromise = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void syncAppBadgeToUnread(userId);
    });

    return () => {
      window.removeEventListener('capacitor:fcmToken', handleFCMToken);
      void tokenListenerPromise.then((l) => l.remove());
      void errorListenerPromise.then((l) => l.remove());
      void tapListenerPromise.then((l) => l.remove());
      void appStateListenerPromise.then((l) => l.remove());
    };
  }, [userId]);
}
